import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCCheckBox, RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCTextArea } from "react-common-components";
import { useForm } from "react-hook-form";
import LovContractItems from "src/components/Lov/ContractItems";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import Audit from "src/components/Audit";
import { addChangeContractItemSchema } from "./schemas";
import { IContracts, IModalAddChangeContractItem, IRefModalAddChangeContractItem } from "./interface";

const ModalAddChangeContractItem: React.ForwardRefRenderFunction<IRefModalAddChangeContractItem, IModalAddChangeContractItem> = ({
	paginationRef,
	efa001Id,
	efa002Id,
	dateStart,
	dateEnd
}, ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const [selectedContractItem, setSelectedContractItem] = useState<IContracts>({} as IContracts);

	// Modal add change contract item
	const [isAdd, setIsAdd] = useState(false);
	const modalAddChangeContractItem = useRef<RCCRefModalProps>(null);
	const {
		reset,
		handleSubmit,
		control,
		clearErrors,
		formState: { errors },
		register,
		setError
	} = useForm();
	const actionButtonAdd = async () => {
		reset();
		setIsAdd(true);
		setSelectedContractItem({} as IContracts);
		modalAddChangeContractItem.current?.openModal();
	};
	const actionButtonChange = async (contract: IContracts) => {
		reset();
		setIsAdd(false);
		setSelectedContractItem(contract);
		modalAddChangeContractItem.current?.openModal();
	};
	const onSubmit = async (data: any) => {
		try{
			clearErrors();
			modalAddChangeContractItem.current?.setLoadingButton();

			const dataForm = {
				...data,
				efa012Dtvalini: dateStart,
				efa012Dtvalfim: dateEnd,
				efa012Efa001Id: efa001Id,
				efa012Efa002Id: efa002Id ?? null,
				efa012Id: selectedContractItem.efa012Id ?? 0,
				efa012Bloqexcedido: data.efa012Bloqexcedido ? "S" : "N"
			};

			await addChangeContractItemSchema(translate).validate({ ...dataForm },{
				abortEarly: false
			});

			if(isAdd)
				await RCCServicesApi.postRequest({
					method: "BusinessGroup/InsertContractItem",
					body: dataForm
				});
			else
				await RCCServicesApi.putRequest({
					method: "BusinessGroup/EditContractItem",
					body: dataForm
				});

			addToast({
				color: "green",
				description: isAdd ? translate("Item de contrato adicionado com sucesso") : translate("Item de contrato alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddChangeContractItem.current?.resetLoadingButton();
			modalAddChangeContractItem.current?.closeModal();
			paginationRef.current?.reload();
		}catch(err){
			modalAddChangeContractItem.current?.resetLoadingButton();
			if(getValidationErrors(err, setError))
				return;

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar item de contrato") : translate("Erro ao alterar item de contrato"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange
	}));
    
	return (
		<Modal
			ref={modalAddChangeContractItem}
			header={{
				title: isAdd ? translate("Adicionar item do contrato") : translate("Alterar item do contrato"),
				icon: { name: "plus" }
			}}
			onSubmit={handleSubmit(onSubmit)}
			withoutForm
			styleInline={`${ModalSide} min-height: 500px; max-height: 500px;`}
		>
			<RCCGrid container item direction="column">
				{!isAdd && (
					<RCCGrid container item justifyContent="flex-end">
						<Audit 
							data={selectedContractItem} 
							type="pop" 
							position="leftEnd"
						/> 
					</RCCGrid>
				)}
				<RCCGrid
					container
					item
					spacing={1}
				>
					<RCCGrid container item xs={6}>
						<LovContractItems 
							control={control}
							name="efa012Tipo"
							label={translate("Tipo")}
							defaultValue={isAdd ? undefined : selectedContractItem.efa012Tipo}	
							error={errors.efa012Tipo}
							required
						/>
					</RCCGrid>
					<RCCGrid container item xs={6}>
						<RCCInput 
							register={register}
							label={translate("Valor")}
							name="efa012Valor"
							maxLength={6}
							defaultValue={isAdd ? undefined : selectedContractItem.efa012Valor}
							type="number"
							step="1"	
							error={errors.efa012Valor}
							required
						/>
					</RCCGrid>
				</RCCGrid>

				<RCCTextArea 
					register={register}
					name="efa012Comentario"
					label={translate("Comentário")}
					maxLength={200}
					defaultValue={isAdd ? undefined : selectedContractItem.efa012Comentario}
					error={errors.efa012Comentario}										
				/>

				<RCCCheckBox 
					name="efa012Bloqexcedido"
					defaultChecked={isAdd ? undefined : selectedContractItem.efa012Bloqexcedido === "S"}
					label={translate("Bloquear excedente")}
					labelPosition="end"
					register={register}	
				/>

			</RCCGrid>
		</Modal>
	);
};

export default forwardRef(ModalAddChangeContractItem);
